import * as React from 'react';
import { RadioGroup } from '@headlessui/react';
import * as t from 'io-ts';
import qs from 'qs';
import { useLocation, useHistory } from 'react-router';
import { pipe } from 'fp-ts/lib/function';
import * as R from 'fp-ts/Record';
import * as O from 'fp-ts/Option';
import { useChooseAvailability } from '../hooks/useChooseAvailability';
import { AvailabilityStatus } from '../generated/graphql';
import { useAlert } from '../contexts/AlertContext';

import { FeatherIcon } from './FeatherIcon';

interface Props {
  currentAvailabililty: AvailabilityStatus;
  playerId: string;
  matchId: string;
}

const availabilities: Record<AvailabilityStatus, AvailabilityStatus> = {
  AVAILABLE: 'AVAILABLE',
  MAYBE: 'MAYBE',
  UNAVAILABLE: 'UNAVAILABLE',
  UNDECLARED: 'UNDECLARED',
};

type RadioChoiceProps = Parameters<typeof RadioGroup.Option>[0];

interface ChoiceProps<T = string> extends Omit<RadioChoiceProps, 'value'> {
  value: T;
  checkedClass?: string;
  activeClass?: string;
}

const AutoChoose = t.type({
  a: t.union([
    t.literal('available'),
    t.literal('maybe'),
    t.literal('unavailable'),
  ]),
});

function CompactOption<T>({
  value,
  children,
  title,
  checkedClass = 'bg-primary-200',
  activeClass = 'ring-primary-300 ring-opacity-60 ',
}: React.PropsWithChildren<ChoiceProps<T>>) {
  return (
    <RadioGroup.Option
      value={value}
      className={({ checked, active }) =>
        `flex-1 rounded-full
        overflow-hidden transition-colors duration-300 focus:outline-none
        text-xs sm:text-sm
         ${checked ? checkedClass : `text-grey-500`} ${
           active ? `ring-2 ${activeClass} z-10` : ''
         }`
      }
    >
      <RadioGroup.Label
        title={title}
        className="flex items-center justify-center py-1 px-1 cursor-pointer break-all"
      >
        {children}
      </RadioGroup.Label>
    </RadioGroup.Option>
  );
}

export const MiniAvailabilityChooser: React.FC<Props> = ({
  currentAvailabililty,
  playerId,
  matchId,
}) => {
  const [chosenAvailability, chooseAvailability] = useChooseAvailability({
    currentAvailabililty,
    playerId,
    matchId,
  });

  const alert = useAlert();
  const location = useLocation();
  const history = useHistory();

  React.useEffect(() => {
    const availability = pipe(
      qs.parse(location.search, { ignoreQueryPrefix: true }),
      AutoChoose.decode,
      O.fromEither,
      O.chain(({ a }) => R.lookup(a.toUpperCase(), availabilities)),
      O.toNullable,
    );

    if (availability && chosenAvailability !== availability) {
      chooseAvailability(availability).then(() => {
        history.replace(location.pathname);
        alert('Availability set');
      });
    }
  }, [location.search]);

  return (
    <div>
      <RadioGroup
        className="inline-flex space-x-2"
        onChange={chooseAvailability}
        value={chosenAvailability}
      >
        <CompactOption
          value={availabilities.AVAILABLE}
          checkedClass="bg-green-100 text-green-700"
          activeClass="ring-green-700"
          title="Available"
        >
          <FeatherIcon icon="check" className="h-5 w-5 " />
        </CompactOption>
        <CompactOption
          value={availabilities.MAYBE}
          checkedClass="bg-amber-200 text-amber-700"
          activeClass="ring-amber-700"
          title="Maybe available"
        >
          <FeatherIcon icon="minus-circle" className="h-5 w-5 " />
        </CompactOption>
        <CompactOption
          value={availabilities.UNAVAILABLE}
          checkedClass="bg-red-100 text-red-700"
          activeClass="ring-red-700"
          title="Unavailable"
        >
          <FeatherIcon icon="x" className="h-5 w-5 " />
        </CompactOption>
      </RadioGroup>
    </div>
  );
};
